import {
    BASE_GROUND_OFFSET,
    BEDROCK_Y,
    DESERT_SANDSTONE_DEPTH,
    DIRT_BUFFER_BELOW_TOP,
    DIRT_LAYER_THICKNESS,
    MESA_STRATA_DEPTH,
    MIN_STONE_TOP_ABS,
    WORLD_HEIGHT,
} from "../config";
import { BLOCK, VoxelWorld, type BiomeMap } from "../types";
import { BIOME_RULES, mesaStrataAt, SURFACE } from "./biomeRules";

export function createWorld(size: number, height: number = WORLD_HEIGHT): VoxelWorld {
    return new VoxelWorld(size, height);
}

export function buildOverworldVoxels(
    heights: number[][],
    biomes: BiomeMap,
    riverMask: boolean[][] | undefined,
    seed: number,
    height?: number,
): VoxelWorld {
    const size = heights.length;
    const world = createWorld(size, height);

    for (let x = 0; x < size; x++) {
        for (let z = 0; z < size; z++) {
            const topAbs = BASE_GROUND_OFFSET + heights[x][z];
            const rules = BIOME_RULES[biomes.biome[x][z]];

            world.set(x, BEDROCK_Y, z, BLOCK.Bedrock);
            const stoneTopAbs = Math.max(
                MIN_STONE_TOP_ABS,
                topAbs - DIRT_LAYER_THICKNESS - DIRT_BUFFER_BELOW_TOP,
            );
            world.fillColumn(x, z, BEDROCK_Y + 1, stoneTopAbs, BLOCK.Stone);
            world.fillColumn(x, z, stoneTopAbs + 1, topAbs - 1, BLOCK.Dirt);

            if (riverMask?.[x][z]) {
                world.set(x, topAbs, z, BLOCK.Sand);
                if (rules.riverFluid) world.set(x, topAbs + 1, z, rules.riverFluid);
                continue;
            }

            switch (rules.surface) {
                case SURFACE.Grass:
                    world.set(x, topAbs, z, BLOCK.Grass);
                    break;
                case SURFACE.SnowyGrass:
                    world.set(x, topAbs, z, BLOCK.Grass);
                    world.set(x, topAbs + 1, z, BLOCK.SnowBlock);
                    break;
                case SURFACE.CoarseDirt:
                    world.set(x, topAbs, z, BLOCK.CoarseDirt);
                    break;
                case SURFACE.Sand:
                    world.set(x, topAbs, z, BLOCK.Sand);
                    world.fillColumn(
                        x,
                        z,
                        Math.max(BEDROCK_Y + 1, topAbs - DESERT_SANDSTONE_DEPTH),
                        topAbs - 1,
                        BLOCK.Sandstone,
                    );
                    break;
                case SURFACE.Terracotta: {
                    const from = Math.max(BEDROCK_Y + 1, topAbs - MESA_STRATA_DEPTH + 1);
                    for (let y = from; y <= topAbs; y++) {
                        world.set(x, y, z, mesaStrataAt(y, seed));
                    }
                    break;
                }
                case SURFACE.Mycelium:
                    world.set(x, topAbs, z, BLOCK.Mycelium);
                    break;
            }
        }
    }

    return world;
}
